import React, { useState, useEffect, useMemo, useRef } from 'react';
import '../abSplitTestV1.css';
import '../abSplitTestV1_addons.css';
import DragEmailEditor from './campaign/DragEmailEditor';
import HtmlEmailEditor from './campaign/HtmlEmailEditor';

const API = '/api/abtest-v1';
const SENDERS_API = '/api/senders';
const SEGMENTS_API = '/api/segments';
const LISTS_API = '/api/lists';
const TAGS_API = '/api/tags';
const LOG = (...a) => console.log('[abtest-v5]', ...a);
const ERR = (...a) => console.error('[abtest-v5]', ...a);

const STEPS = ['Sender', 'Design', 'Content', 'Recipients', 'Review & Confirm', 'Schedule'];
const TABS = ['All', 'Draft', 'Scheduled', 'In Progress', 'Sent'];

const EDITORS = [
  { key: 'drag', title: 'Drag & drop editor', desc: 'Effortlessly design beautiful emails with our simple drag-and-drop editor.', icon: 'palette' },
  { key: 'ai', title: 'SendX AI Editor', desc: 'A modern, block-based editor that makes writing and designing emails as seamless as typing a document.', icon: 'ai', badge: 'BETA' },
  { key: 'html', title: 'Plain Text / HTML editor', desc: 'Write plain text or paste custom HTML for full control over your email design.', icon: 'code' }
];

const CUSTOM_FIELDS = [
  { key: '{{first_name}}', label: 'First Name' },
  { key: '{{last_name}}', label: 'Last Name' },
  { key: '{{name}}', label: 'Full Name' },
  { key: '{{email}}', label: 'Email' },
  { key: '{{company}}', label: 'Company' }
];

const TEST_SIZES = [5, 10, 15, 20, 25, 30, 40, 50];
const DELAY_UNITS = ['Hours', 'Days', 'Weeks'];

const TIMEZONES = [
  'UTC', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Shanghai',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Toronto',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
  'Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland',
  'Africa/Cairo', 'Africa/Johannesburg', 'Antarctica/Mawson'
];

const DEFAULT_HTML = '<html>\n<head><style>body{font-family:Arial,sans-serif;padding:20px}</style></head>\n<body>\n<h2>Hi {{first_name}},</h2>\n<p>Welcome to our A/B split test campaign!</p>\n<p>Click <a href="#">here</a> to learn more.</p>\n<p>Best,<br/>Team</p>\n</body>\n</html>';

function emptyForm() {
  return {
    name: '',
    senderId: '',
    variations: [
      { subject: '', previewText: '', previewTextShow: false },
      { subject: '', previewText: '', previewTextShow: false }
    ],
    editor: 'html',
    templateId: '',
    htmlContent: DEFAULT_HTML,
    recipientContacts: [], // array of {type, id, label} tags
    excludeContacts: [],
    testSizePct: 10,
    winnerDelayValue: 1,
    winnerDelayUnit: 'Days',
    sendMode: 'now',
    scheduledDate: '',
    scheduledTime: '',
    timezone: 'Asia/Karachi',
    gaTracking: false
  };
}

function fmtStatus(s) {
  const m = { draft: 'DRAFT', scheduled: 'SCHEDULED', running: 'RUNNING', in_progress: 'IN PROGRESS', sent: 'SENT', archived: 'ARCHIVED' };
  return m[s] || (s || 'draft').toUpperCase();
}

function tabMatches(test, tab) {
  const s = (test.status || 'draft').toLowerCase();
  if (tab === 'All') return s !== 'archived';
  if (tab === 'Draft') return s === 'draft';
  if (tab === 'Scheduled') return s === 'scheduled';
  if (tab === 'In Progress') return s === 'running' || s === 'in_progress';
  if (tab === 'Sent') return s === 'sent';
  return true;
}

function getUtcOffset(tz, dateStr) {
  try {
    const d = dateStr ? new Date(dateStr + 'T12:00:00Z') : new Date();
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longOffset' }).formatToParts(d);
    const off = (parts.find(p => p.type === 'timeZoneName') || {}).value || '';
    if (off.startsWith('GMT')) return '(' + off + ')';
    return '(GMT ' + off + ')';
  } catch(e) { return '(GMT ?)'; }
}

function fmtSendDate(dateStr, timeStr, tz) {
  try {
    if (!dateStr) return '';
    const iso = dateStr + 'T' + (timeStr || '09:00') + ':00';
    const d = new Date(iso);
    const opts = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
    return new Intl.DateTimeFormat('en-US', opts).format(d) + ' ' + tz;
  } catch(e) { return dateStr + ' ' + (timeStr || '') + ' ' + tz; }
}

function Icon({ name, size = 20 }) {
  const s = { width: size, height: size, display: 'inline-block', verticalAlign: 'middle', lineHeight: 1 };
  if (name === 'palette') return <span style={s}>🎨</span>;
  if (name === 'ai') return <span style={s}>✨</span>;
  if (name === 'code') return <span style={{ ...s, fontFamily: 'monospace', fontWeight: 700 }}>&lt;/&gt;</span>;
  if (name === 'info') return <span style={s} title="Info">ⓘ</span>;
  if (name === 'sparkle') return <span style={s}>✨</span>;
  if (name === 'warn') return <span style={s}>⚠</span>;
  return null;
}

export default function ABSplitTest() {
  const [view, setView] = useState('list');
  const [tests, setTests] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [senders, setSenders] = useState([]);
  const [segments, setSegments] = useState([]);
  const [lists, setLists] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeTab, setActiveTab] = useState('All');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showCFDropdown, setShowCFDropdown] = useState(-1);
  const [showRecipDropdown, setShowRecipDropdown] = useState(false);
  const [showExcludeDropdown, setShowExcludeDropdown] = useState(false);
  const [contentEditMode, setContentEditMode] = useState(true);
  const [templatePage, setTemplatePage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [detailTest, setDetailTest] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => { loadTests(); loadTemplates(); loadSenders(); loadSegments(); loadLists(); loadTags(); }, []);

  useEffect(() => {
    const onDocClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpenId(null); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const api = async (path, opts = {}) => {
    const r = await fetch(path, { headers: { 'Content-Type': 'application/json' }, credentials: 'include', ...opts });
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  };
  const loadTests = async () => { setLoading(true); try { const d = await api(API); setTests(d.items || []); } catch(e) { ERR('loadTests', e); setErr('Failed to load tests'); } finally { setLoading(false); } };
  const loadTemplates = async () => { try { const d = await api(API + '/templates'); setTemplates(d.templates || []); } catch(e) { ERR('loadTemplates', e); } };
  const loadSenders = async () => { try { const d = await api(SENDERS_API); setSenders(d.senders || d.items || (Array.isArray(d) ? d : [])); } catch(e) { ERR('loadSenders', e); setSenders([]); } };
  const loadSegments = async () => { try { const d = await api(SEGMENTS_API); setSegments(d.segments || d.items || (Array.isArray(d) ? d : [])); } catch(e) { setSegments([]); } };
  const loadLists = async () => { try { const d = await api(LISTS_API); setLists(d.lists || d.items || (Array.isArray(d) ? d : [])); } catch(e) { setLists([]); } };
  const loadTags = async () => { try { const d = await api(TAGS_API); setTags(d.tags || d.items || (Array.isArray(d) ? d : [])); } catch(e) { setTags([]); } };

  const flash = (msg, isErr) => { if (isErr) setErr(msg); else setOk(msg); setTimeout(() => { setOk(''); setErr(''); }, 3500); };

  const startNewTest = () => { setForm(emptyForm()); setEditingId(null); setWizardStep(0); setView('wizard'); };
  const editExisting = (t) => { setForm({ ...emptyForm(), ...t }); setEditingId(t._id); setWizardStep(0); setView('wizard'); };
  const cancelWizard = () => { setView('list'); setForm(emptyForm()); setEditingId(null); setWizardStep(0); };
  const goNext = () => { if (wizardStep < STEPS.length - 1) setWizardStep(wizardStep + 1); };
  const goBack = () => { if (wizardStep > 0) setWizardStep(wizardStep - 1); };
  const goToStep = (i) => { if (i >= 0 && i <= wizardStep) setWizardStep(i); };

  const saveDraft = async () => {
    setLoading(true);
    try {
      const body = { ...form, status: 'draft' };
      const url = editingId ? (API + '/' + editingId) : API;
      const method = editingId ? 'PUT' : 'POST';
      await api(url, { method, body: JSON.stringify(body) });
      flash('Draft saved'); await loadTests(); cancelWizard();
    } catch(e) { ERR('saveDraft', e); flash('Failed to save draft', true); }
    finally { setLoading(false); }
  };

  const submitTest = async () => {
    setLoading(true);
    try {
      const body = { ...form, status: form.sendMode === 'now' ? 'in_progress' : 'scheduled' };
      const url = editingId ? (API + '/' + editingId) : API;
      const method = editingId ? 'PUT' : 'POST';
      await api(url, { method, body: JSON.stringify(body) });
      flash('A/B Split Test submitted'); await loadTests(); cancelWizard();
    } catch(e) { ERR('submitTest', e); flash('Failed to submit', true); }
    finally { setLoading(false); setConfirmOpen(false); }
  };

  const duplicateTest = async (id) => { try { await api(API + '/' + id + '/duplicate', { method: 'POST' }); await loadTests(); flash('Duplicated'); } catch(e) { flash('Failed to duplicate', true); } };
  const archiveTest = async (id) => { if (!window.confirm('Archive this A/B split test?')) return; try { await api(API + '/' + id + '/archive', { method: 'POST' }); await loadTests(); flash('Archived'); } catch(e) { flash('Failed to archive', true); } };
  const deleteTest = async (id) => {
    if (!window.confirm('Delete this A/B split test permanently?')) return;
    try { await api(API + '/' + id, { method: 'DELETE' }); await loadTests(); setSelectedIds(s => s.filter(x => x !== id)); flash('Deleted'); }
    catch(e) { ERR('deleteTest', e); flash('Failed to delete', true); }
  };
  const bulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm('Delete ' + selectedIds.length + ' selected test(s)?')) return;
    try {
      for (const id of selectedIds) { try { await api(API + '/' + id, { method: 'DELETE' }); } catch(e) { ERR('bulkDelete', id, e); } }
      await loadTests(); setSelectedIds([]); flash('Deleted');
    } catch(e) { flash('Failed to bulk delete', true); }
  };
  const toggleSelect = (id) => setSelectedIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleSelectAllVisible = (filtered) => {
    const ids = filtered.map(t => t._id);
    const allSelected = ids.length > 0 && ids.every(id => selectedIds.includes(id));
    setSelectedIds(allSelected ? selectedIds.filter(x => !ids.includes(x)) : Array.from(new Set([...selectedIds, ...ids])));
  };
  const openDetail = (t) => { setDetailTest(t); setMenuOpenId(null); };
  const closeDetail = () => setDetailTest(null);

  const updateVariation = (i, patch) => { const v = [...form.variations]; v[i] = { ...v[i], ...patch }; setForm({ ...form, variations: v }); };
  const addVariation = () => { if (form.variations.length >= 5) return; setForm({ ...form, variations: [...form.variations, { subject: '', previewText: '', previewTextShow: false }] }); };
  const removeVariation = (i) => { if (form.variations.length <= 2) return; setForm({ ...form, variations: form.variations.filter((_, idx) => idx !== i) }); };
  const insertCF = (varIdx, cfKey) => { const cur = form.variations[varIdx].subject || ''; updateVariation(varIdx, { subject: cur + ' ' + cfKey }); setShowCFDropdown(-1); };

  const addRecipient = (item) => {
    if (!item) return;
    const exists = form.recipientContacts.some(r => r.type === item.type && r.id === item.id);
    if (!exists) setForm({ ...form, recipientContacts: [...form.recipientContacts, item] });
    setShowRecipDropdown(false);
  };
  const removeRecipient = (item) => setForm({ ...form, recipientContacts: form.recipientContacts.filter(r => !(r.type === item.type && r.id === item.id)) });
  const addExclude = (item) => {
    if (!item) return;
    const exists = form.excludeContacts.some(r => r.type === item.type && r.id === item.id);
    if (!exists) setForm({ ...form, excludeContacts: [...form.excludeContacts, item] });
    setShowExcludeDropdown(false);
  };
  const removeExclude = (item) => setForm({ ...form, excludeContacts: form.excludeContacts.filter(r => !(r.type === item.type && r.id === item.id)) });

  const availableRecipients = useMemo(() => {
    const items = [];
    segments.forEach(s => items.push({ type: 'segment', id: s._id || s.id, label: (s.name || s.title) + ' - [segment]' }));
    lists.forEach(l => items.push({ type: 'list', id: l._id || l.id, label: (l.name || l.title) + ' - [list]' }));
    tags.forEach(t => items.push({ type: 'tag', id: t._id || t.id, label: (t.name || t.title) + ' - [tag]' }));
    return items;
  }, [segments, lists, tags]);

  const eligibleCount = useMemo(() => {
    if (form.recipientContacts.length === 0) return 0;
    // approximate: sum of member counts if available
    let n = 0;
    form.recipientContacts.forEach(r => {
      const pool = r.type === 'segment' ? segments : (r.type === 'list' ? lists : tags);
      const found = pool.find(p => (p._id || p.id) === r.id);
      if (found) n += (found.memberCount || found.count || 1);
    });
    return n;
  }, [form.recipientContacts, segments, lists, tags]);

  const currentSender = senders.find(s => (s._id || s.id) === form.senderId);
  const senderLabel = currentSender ? ((currentSender.name || currentSender.fromName || 'Sender') + ' <' + (currentSender.email || currentSender.fromEmail || '') + '>') : '';

  const validateStep = (step) => {
    if (step === 0) {
      if (!form.name.trim()) return 'Please enter A/B split test name';
      if (!form.senderId) return 'Please select a sender';
      if (!form.variations[0].subject.trim()) return 'Variation A subject is required';
      if (!form.variations[1].subject.trim()) return 'Variation B subject is required';
    }
    if (step === 2) { if (!form.htmlContent.trim()) return 'Please add email content'; }
    if (step === 3) { if (form.recipientContacts.length === 0) return 'Please select at least one recipient list/tag/segment'; }
    if (step === 5) { if (form.sendMode === 'later' && !form.scheduledDate) return 'Please pick a schedule date'; }
    return null;
  };
  const tryNext = () => { const e = validateStep(wizardStep); if (e) { flash(e, true); return; } goNext(); };

  // ==================== LIST VIEW ====================
  const renderStepper = () => (
    <div className="ab1-stepper">
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <span className={'ab1-crumb ' + (i === wizardStep ? 'active' : (i < wizardStep ? 'done' : ''))} onClick={() => goToStep(i)}>{s}</span>
          {i < STEPS.length - 1 && <span className="ab1-crumb-sep">›</span>}
        </React.Fragment>
      ))}
    </div>
  );

  const renderEmptyState = () => (
    <div className="ab1-empty-hero">
      <div className="ab1-empty-illus">
        <div className="ab1-empty-card ab1-empty-card-a"><div className="ab1-empty-tag">A</div><div className="ab1-empty-lines"><i/><i/><i/></div></div>
        <div className="ab1-empty-card ab1-empty-card-b"><div className="ab1-empty-tag">B</div><div className="ab1-empty-lines"><i/><i/><i/></div></div>
        <div className="ab1-empty-winner">🏆 Winner Scheduled</div>
      </div>
      <div className="ab1-empty-body">
        <h2>Test what works. Send what wins.</h2>
        <ul>
          <li>��� Split your audience and try different subject lines, senders, or content variations.</li>
          <li>✓ Automatically send the winning email to the rest of your list.</li>
          <li>✓ Learn what resonates with your audience and boost engagement.</li>
        </ul>
        <button className="ab1-btn primary lg" onClick={startNewTest}>+ Create A/B Split Test</button>
      </div>
    </div>
  );

  const renderTestList = () => {
    const filtered = tests.filter(t => tabMatches(t, activeTab));
    const counts = TABS.reduce((acc, tab) => { acc[tab] = tests.filter(t => tabMatches(t, tab)).length; return acc; }, {});
    const filteredIds = filtered.map(t => t._id);
    const allVisibleSelected = filteredIds.length > 0 && filteredIds.every(id => selectedIds.includes(id));
    return (
      <>
        <div className="ab1-page-head">
          <h1 className="ab1-page-title">A/B Split Test</h1>
          <div className="ab1-page-head-right">
            {selectedIds.length > 0 && (
              <button className="ab1-btn danger" onClick={bulkDelete}>Delete ({selectedIds.length})</button>
            )}
            <button className="ab1-btn primary" onClick={startNewTest}>+ Create A/B Split Test</button>
          </div>
        </div>
        <div className="ab1-tabs-underline">
          {TABS.map(tab => (
            <button key={tab} className={'ab1-tab ' + (activeTab === tab ? 'active' : '')} onClick={() => setActiveTab(tab)}>{tab}{counts[tab] > 0 && <span className="ab1-tab-count">{counts[tab]}</span>}</button>
          ))}
        </div>
        <div className="ab1-tests-list ab1-tests-list-v6">
          {filtered.length === 0 ? (
            <div className="ab1-nodata"><div className="ab1-nodata-icon">📭</div><p>No {activeTab === 'All' ? '' : activeTab.toLowerCase()} A/B splits yet.</p></div>
          ) : (
            <>
              <div className="ab1-list-header">
                <label className="ab1-check-cell" onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={allVisibleSelected} onChange={() => toggleSelectAllVisible(filtered)} />
                </label>
                <div className="ab1-col-name">Name</div>
                <div className="ab1-col-status">Status</div>
                <div className="ab1-col-metric">Opens</div>
                <div className="ab1-col-metric">Clicks</div>
                <div className="ab1-col-metric">Sent</div>
                <div className="ab1-col-actions"></div>
              </div>
              {filtered.map(t => {
                const isSelected = selectedIds.includes(t._id);
                const stats = t.stats || {};
                const opens = stats.opens != null ? stats.opens : (t.opens != null ? t.opens : 0);
                const clicks = stats.clicks != null ? stats.clicks : (t.clicks != null ? t.clicks : 0);
                const sent = stats.sent != null ? stats.sent : (t.sent != null ? t.sent : 0);
                const menuOpen = menuOpenId === t._id;
                return (
                  <div key={t._id} className={'ab1-test-row-v6 ' + (isSelected ? 'selected' : '')} onClick={(e) => { if (e.target.closest && e.target.closest('.ab1-no-detail')) return; openDetail(t); }}>
                    <label className="ab1-check-cell ab1-no-detail" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(t._id)} />
                    </label>
                    <div className="ab1-col-name">
                      <div className="ab1-row-title">{t.name || '(unnamed)'}</div>
                      <div className="ab1-row-sub">{(t.variations || []).length} variations · Test size {t.testSizePct || 10}%</div>
                    </div>
                    <div className="ab1-col-status"><span className={'ab1-status ' + (t.status || 'draft')}>{fmtStatus(t.status || 'draft')}</span></div>
                    <div className="ab1-col-metric">{opens}</div>
                    <div className="ab1-col-metric">{clicks}</div>
                    <div className="ab1-col-metric">{sent}</div>
                    <div className="ab1-col-actions ab1-no-detail" onClick={e => e.stopPropagation()}>
                      <div className="ab1-menu-wrap" ref={menuOpen ? menuRef : null}>
                        <button className="ab1-dots-btn" title="More actions" onClick={() => setMenuOpenId(menuOpen ? null : t._id)}>⋯</button>
                        {menuOpen && (
                          <div className="ab1-dots-menu">
                            <button className="ab1-dots-item" onClick={() => { setMenuOpenId(null); openDetail(t); }}>View</button>
                            <button className="ab1-dots-item" onClick={() => { setMenuOpenId(null); editExisting(t); }}>Edit</button>
                            <button className="ab1-dots-item" onClick={() => { setMenuOpenId(null); duplicateTest(t._id); }}>Duplicate</button>
                            <button className="ab1-dots-item" onClick={() => { setMenuOpenId(null); archiveTest(t._id); }}>Archive</button>
                            <button className="ab1-dots-item danger" onClick={() => { setMenuOpenId(null); deleteTest(t._id); }}>Delete</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </>
    );
  };

  const renderListView = () => (tests.length === 0 ? renderEmptyState() : renderTestList());

  // ==================== STEP 1: SENDER ====================
  const renderStepSender = () => (
    <div className="ab1-card-form">
      <div className="ab1-field">
        <label>A/B split test name <span className="req">*</span></label>
        <div className="ab1-hint">Used to identify this campaign in your dashboard.</div>
        <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
      </div>

      <div className="ab1-field">
        <label>Sender <span className="req">*</span></label>
        <div className="ab1-hint">This will be displayed in the From field of your recipient's email client.</div>
        {senders.length === 0 ? (
          <div className="ab1-info-banner">No senders found. Please add a sender in Settings first.</div>
        ) : (
          <select value={form.senderId} onChange={e => setForm({ ...form, senderId: e.target.value })}>
            <option value="">— Select a sender —</option>
            {senders.map(s => { const id = s._id || s.id; const name = s.name || s.fromName || 'Sender'; const email = s.email || s.fromEmail || ''; return (<option key={id} value={id}>{name} &lt; {email} &gt;</option>); })}
          </select>
        )}
      </div>

      <div className="ab1-field">
        <label>A/B Variations <span className="req">*</span></label>
        {form.variations.map((v, i) => (
          <div key={i} className="ab1-variation">
            <div className="ab1-var-row">
              <div className="ab1-var-label">Variation {String.fromCharCode(65 + i)} <span className="req">*</span> :</div>
              <input type="text" className="ab1-var-input" value={v.subject} onChange={e => updateVariation(i, { subject: e.target.value })} placeholder={'Subject line ' + String.fromCharCode(65 + i)} />
              <button className="ab1-icon-btn" title="AI subject suggestion" onClick={() => updateVariation(i, { subject: (v.subject || '') + ' ✨' })}><Icon name="sparkle" size={14} /></button>
              <div className="ab1-cf-wrap">
                <button className="ab1-cf-btn" onClick={() => setShowCFDropdown(showCFDropdown === i ? -1 : i)}>Custom Fields <span className="ab1-caret">▾</span></button>
                {showCFDropdown === i && (
                  <div className="ab1-cf-dropdown">
                    <div className="ab1-cf-title">Custom Fields</div>
                    {CUSTOM_FIELDS.map(cf => (<button key={cf.key} className="ab1-cf-item" onClick={() => insertCF(i, cf.key)}><span>{cf.label}</span><span className="ab1-cf-key">{cf.key}</span></button>))}
                  </div>
                )}
              </div>
              <button className="ab1-icon-btn" title="Info"><Icon name="info" size={14} /></button>
              {form.variations.length > 2 && (<button className="ab1-icon-btn danger" title="Remove" onClick={() => removeVariation(i)}>×</button>)}
            </div>
            {v.previewTextShow ? (
              <div className="ab1-var-preview">
                <input type="text" value={v.previewText} onChange={e => updateVariation(i, { previewText: e.target.value })} placeholder="Preview text (appears after subject in inbox)" />
                <button className="ab1-linkbtn" onClick={() => updateVariation(i, { previewTextShow: false, previewText: '' })}>− Remove preview text</button>
              </div>
            ) : (<button className="ab1-linkbtn" onClick={() => updateVariation(i, { previewTextShow: true })}>+ Add preview text</button>)}
          </div>
        ))}
        {form.variations.length < 5 && (<button className="ab1-linkbtn" onClick={addVariation}>+ Add variation</button>)}
      </div>
    </div>
  );

  // ==================== STEP 2: TYPE ====================
  const renderStepType = () => (
    <div className="ab1-card-center">
      <div className="ab1-panel-md">
        <div className="ab1-panel-title">Select email editor</div>
        <div className="ab1-editor-cards">
          {EDITORS.map(ed => (
            <div key={ed.key} className={'ab1-editor-card ' + (form.editor === ed.key ? 'selected' : '')} onClick={() => setForm({ ...form, editor: ed.key })}>
              <div className="ab1-editor-thumb"><Icon name={ed.icon} size={32} /></div>
              <div className="ab1-editor-body">
                <div className="ab1-editor-title">{ed.title} {ed.badge && <span className="ab1-badge">{ed.badge}</span>}</div>
                <div className="ab1-editor-desc">{ed.desc}</div>
                <div className="ab1-editor-arrow">→</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ==================== STEP 3: DESIGN ====================
  const renderStepDesign = () => {
    const perPage = 6;
    const total = templates.length + 1; // +1 for Empty card
    const startIdx = (templatePage - 1) * perPage;
    const endIdx = Math.min(startIdx + perPage, total);
    // Build items with Empty card first
    const allItems = [{ __empty: true }].concat(templates);
    const pageItems = allItems.slice(startIdx, endIdx);
    return (
      <div className="ab1-design-simple">
        <div className="ab1-editor-picker">
          <div className="ab1-panel-title">1. Choose email editor</div>
          <div className="ab1-editor-cards">
            {EDITORS.map(ed => (
              <div key={ed.key} className={'ab1-editor-card ' + (form.editor === ed.key ? 'selected' : '')} onClick={() => setForm({ ...form, editor: ed.key })}>
                <div className="ab1-editor-thumb"><Icon name={ed.icon} size={28} /></div>
                <div className="ab1-editor-body">
                  <div className="ab1-editor-title">{ed.title} {ed.badge && <span className="ab1-badge">{ed.badge}</span>}</div>
                  <div className="ab1-editor-desc">{ed.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="ab1-panel-title" style={{marginTop:'20px'}}>2. Choose a template</div>
        <div className="ab1-tpl-grid-simple">
          {pageItems.map((t, idx) => {
            if (t.__empty) return (
              <div key="empty" className={'ab1-tpl-card ab1-tpl-empty ' + (form.templateId === 'empty' ? 'selected' : '')} onClick={() => setForm({ ...form, templateId: 'empty', htmlContent: DEFAULT_HTML })}>
                <div className="ab1-tpl-thumb"><div className="ab1-tpl-empty-icon">&lt;/&gt;</div><div className="ab1-tpl-empty-text">Paste your HTML</div></div>
                <div className="ab1-tpl-name">Empty</div>
              </div>
            );
            const tid = t._id || t.key || t.name;
            const previewHtml = t.htmlContent || t.html || t.body || '';
            return (
              <div key={tid} className={'ab1-tpl-card ' + (form.templateId === tid ? 'selected' : '')} onClick={() => setForm({ ...form, templateId: tid, htmlContent: previewHtml || form.htmlContent })}>
                <div className="ab1-tpl-thumb ab1-tpl-thumb-illus">
                  {previewHtml ? (
                    <div className="ab1-tpl-iframe-wrap">
                      <iframe className="ab1-tpl-preview-iframe" title={t.name || 'Template'} srcDoc={previewHtml} scrolling="no" tabIndex={-1} />
                      <div className="ab1-tpl-iframe-shield" />
                    </div>
                  ) : (
                    <span className="ab1-tpl-emoji-lg">{t.thumbEmoji || '📧'}</span>
                  )}
                </div>
                <div className="ab1-tpl-name">{t.name || 'Untitled'}</div>
                {t.category && <div className="ab1-tpl-cat">{t.category}</div>}
              </div>
            );
          })}
        </div>
        <div className="ab1-tpl-footer">
          <div className="ab1-tpl-count">Showing {startIdx + 1} to {endIdx} of {total} results</div>
          <div className="ab1-tpl-nav">
            <button className="ab1-icon-btn" onClick={() => setTemplatePage(Math.max(1, templatePage - 1))} disabled={templatePage === 1}>←</button>
            <button className="ab1-icon-btn" onClick={() => setTemplatePage(endIdx < total ? templatePage + 1 : templatePage)} disabled={endIdx >= total}>→</button>
          </div>
        </div>
      </div>
    );
  };

  // ==================== STEP 4: CONTENT (full-screen) ====================
  const renderStepContent = () => (
    <div className="ab1-content-fullscreen">
      <div className="ab1-content-topbar">
        <button className="ab1-btn ghost" onClick={goBack}>‹ Back</button>
        <div className="ab1-content-topcenter">
          <label className="ab1-edit-toggle">
            <span>Edit Mode</span>
            <label className="ab1-switch"><input type="checkbox" checked={contentEditMode} onChange={e => setContentEditMode(e.target.checked)} /><span className="ab1-slider" /></label>
          </label>
        </div>
        <div className="ab1-content-topright">
          <button className="ab1-btn ghost" onClick={() => flash('Test email sent (mock)')}>Send Test Email</button>
          <button className="ab1-btn ghost" onClick={saveDraft}>Save & Exit</button>
          <button className="ab1-btn primary" onClick={tryNext}>Continue</button>
        </div>
      </div>
      <div className="ab1-content-editor-wrap">
        {form.editor === 'drag' ? (
          <DragEmailEditor
            value={form.htmlContent}
            htmlContent={form.htmlContent}
            html={form.htmlContent}
            content={form.htmlContent}
            initial={{ title: form.name || 'Untitled', category: 'Custom', html: form.htmlContent, htmlContent: form.htmlContent, content: form.htmlContent }}
            onChange={(v) => setForm({ ...form, htmlContent: typeof v === 'string' ? v : ((v && (v.html || v.htmlContent || v.content)) || form.htmlContent) })}
            onHtmlContentChange={(html) => setForm({ ...form, htmlContent: html })}
            onHtmlChange={(html) => setForm({ ...form, htmlContent: html })}
            onSave={async (data) => {
              const html = (data && (data.html || data.htmlContent || data.content)) || form.htmlContent;
              setForm({ ...form, htmlContent: html });
              if (data && (data.title || data.name)) {
                try {
                  await api('/templates', { method: 'POST', body: JSON.stringify({ name: data.title || data.name, category: data.category || 'Custom', htmlContent: html }) });
                  flash('Template saved');
                } catch (e) {
                  try {
                    const stored = JSON.parse(localStorage.getItem('abtest_local_templates') || '[]');
                    stored.push({ _id: 'local-' + Date.now(), name: data.title || data.name, category: data.category || 'Custom', htmlContent: html, createdAt: new Date().toISOString() });
                    localStorage.setItem('abtest_local_templates', JSON.stringify(stored));
                    flash('Template saved locally');
                  } catch (e2) { flash('Content saved (template save failed)'); }
                }
              } else { flash('Content saved'); }
              tryNext();
            }}
            onCancel={() => { flash('Cancelled'); goBack(); }}
            onClose={() => { flash('Closed'); goBack(); }}
            readOnly={!contentEditMode}
          />
        ) : form.editor === 'html' ? (
          <HtmlEmailEditor
            value={form.htmlContent}
            htmlContent={form.htmlContent}
            html={form.htmlContent}
            content={form.htmlContent}
            initial={{ title: form.name || 'Untitled', category: 'Custom', html: form.htmlContent, htmlContent: form.htmlContent, content: form.htmlContent }}
            onChange={(v) => setForm({ ...form, htmlContent: typeof v === 'string' ? v : ((v && (v.html || v.htmlContent || v.content)) || form.htmlContent) })}
            onHtmlContentChange={(html) => setForm({ ...form, htmlContent: html })}
            onHtmlChange={(html) => setForm({ ...form, htmlContent: html })}
            onSave={async (data) => {
              const html = (data && (data.html || data.htmlContent || data.content)) || form.htmlContent;
              setForm({ ...form, htmlContent: html });
              if (data && (data.title || data.name)) {
                try {
                  await api('/templates', { method: 'POST', body: JSON.stringify({ name: data.title || data.name, category: data.category || 'Custom', htmlContent: html }) });
                  flash('Template saved');
                } catch (e) {
                  try {
                    const stored = JSON.parse(localStorage.getItem('abtest_local_templates') || '[]');
                    stored.push({ _id: 'local-' + Date.now(), name: data.title || data.name, category: data.category || 'Custom', htmlContent: html, createdAt: new Date().toISOString() });
                    localStorage.setItem('abtest_local_templates', JSON.stringify(stored));
                    flash('Template saved locally');
                  } catch (e2) { flash('Content saved (template save failed)'); }
                }
              } else { flash('Content saved'); }
              tryNext();
            }}
            onCancel={() => { flash('Cancelled'); goBack(); }}
            onClose={() => { flash('Closed'); goBack(); }}
            readOnly={!contentEditMode}
          />
        ) : (
          <div className="ab1-content-split">
            <div className="ab1-content-editor">
              <textarea className="ab1-html-textarea" value={form.htmlContent} onChange={e => setForm({ ...form, htmlContent: e.target.value })} readOnly={!contentEditMode} spellCheck={false} placeholder="Write your email content or paste HTML..." />
            </div>
            <div className="ab1-content-preview">
              <iframe className="ab1-preview-iframe" title="preview" srcDoc={form.htmlContent} />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ==================== STEP 5: RECIPIENTS ====================
  const renderStepRecipients = () => (
    <div className="ab1-card-form">
      <div className="ab1-field">
        <label>Recipient contacts <span className="req">*</span></label>
        <div className="ab1-hint">Select contacts to receive your campaign.</div>
        <div className="ab1-multi-wrap" onClick={() => setShowRecipDropdown(true)}>
          <div className="ab1-multi-tags">
            {form.recipientContacts.map(r => (<span key={r.type + r.id} className="ab1-multi-tag">{r.label}<button onClick={e => { e.stopPropagation(); removeRecipient(r); }}>×</button></span>))}
            <input type="text" placeholder={form.recipientContacts.length === 0 ? 'Select segments, lists, or tags...' : ''} readOnly onFocus={() => setShowRecipDropdown(true)} />
          </div>
          <span className="ab1-caret">▾</span>
        </div>
        {showRecipDropdown && (
          <div className="ab1-multi-dropdown">
            <div className="ab1-multi-dropdown-head"><b>Choose from</b><button className="ab1-icon-btn" onClick={() => setShowRecipDropdown(false)}>×</button></div>
            {availableRecipients.length === 0 ? (<div className="ab1-nodata sm">No segments/lists/tags found.</div>) : availableRecipients.map(item => (
              <button key={item.type + item.id} className="ab1-multi-item" onClick={() => addRecipient(item)}>{item.label}</button>
            ))}
          </div>
        )}
      </div>

      <div className="ab1-warn-banner amber">
        <div><b>Eligible Recipients: {eligibleCount}</b></div>
        <div>Contacts that have bounced, unsubscribed, or marked spam are automatically excluded from this count.</div>
      </div>

      {senders.some(s => (s.isDemo || (s.email || '').includes('tryoutsendx'))) && (
        <div className="ab1-warn-banner amber">
          <div className="ab1-warn-row">
            <div>
              <b>Demo sender limit: 5 of 10 emails used</b>
              <div>You can still send to 5 contacts on this demo sender. Add your own sending domain to remove this limit.</div>
            </div>
            <button className="ab1-btn ghost sm" onClick={() => window.open('/#/Domain', '_blank')}>Add sending domain</button>
          </div>
        </div>
      )}

      <div className="ab1-field">
        <label>Exclude contacts</label>
        <div className="ab1-hint">Your campaign will not be sent to these contacts.</div>
        <div className="ab1-multi-wrap" onClick={() => setShowExcludeDropdown(true)}>
          <div className="ab1-multi-tags">
            {form.excludeContacts.map(r => (<span key={r.type + r.id} className="ab1-multi-tag">{r.label}<button onClick={e => { e.stopPropagation(); removeExclude(r); }}>×</button></span>))}
            <input type="text" placeholder={form.excludeContacts.length === 0 ? 'Select from available lists, tags or segments' : ''} readOnly />
          </div>
          <span className="ab1-caret">▾</span>
        </div>
        {showExcludeDropdown && (
          <div className="ab1-multi-dropdown">
            <div className="ab1-multi-dropdown-head"><b>Exclude from</b><button className="ab1-icon-btn" onClick={() => setShowExcludeDropdown(false)}>×</button></div>
            {availableRecipients.length === 0 ? (<div className="ab1-nodata sm">No segments/lists/tags found.</div>) : availableRecipients.map(item => (
              <button key={item.type + item.id} className="ab1-multi-item" onClick={() => addExclude(item)}>{item.label}</button>
            ))}
          </div>
        )}
      </div>

      <div className="ab1-field">
        <label>Test audience size <Icon name="info" size={14} /></label>
        <select value={form.testSizePct} onChange={e => setForm({ ...form, testSizePct: Number(e.target.value) })}>
          {TEST_SIZES.map(s => (<option key={s} value={s}>{s}</option>))}
        </select>
      </div>

      <div className="ab1-field">
        <label>Automatically send the winning variation to remaining contacts after <Icon name="info" size={14} /></label>
        <div className="ab1-inline-2">
          <select value={form.winnerDelayValue} onChange={e => setForm({ ...form, winnerDelayValue: Number(e.target.value) })}>
            {[1,2,3,4,5,6,7,10,14,21,30].map(v => (<option key={v} value={v}>{v}</option>))}
          </select>
          <select value={form.winnerDelayUnit} onChange={e => setForm({ ...form, winnerDelayUnit: e.target.value })}>
            {DELAY_UNITS.map(u => (<option key={u} value={u}>{u}</option>))}
          </select>
        </div>
      </div>
    </div>
  );

  // ==================== STEP 6: REVIEW ====================
  const renderStepReview = () => {
    const editorName = (EDITORS.find(e => e.key === form.editor) || {}).title || form.editor || '—';
    const templateName = form.templateId === 'empty' ? 'Empty (Custom HTML)' : ((templates.find(t => (t._id || t.key || t.name) === form.templateId) || {}).name || form.templateId || '—');
    const scheduleText = form.sendMode === 'now' ? 'Send now (immediately after submit)' : (form.scheduledDate ? fmtSendDate(form.scheduledDate, form.scheduledTime, form.timezone) : 'Not scheduled yet');
    return (
      <div className="ab1-review-simple">
        <div className="ab1-review-card">
          <div className="ab1-review-title"><span>A/B Variations & Sender</span><button className="ab1-linkbtn" onClick={() => goToStep(0)}>Edit</button></div>
          <div className="ab1-review-body">
            <div className="ab1-review-name">{form.name || '(unnamed)'}</div>
            <div className="ab1-kv"><span>Campaign name</span><b>{form.name || '—'}</b></div>
            <div className="ab1-kv"><span>Sender</span><b>{senderLabel || '—'}</b></div>
            <div className="ab1-kv"><span>Variations</span><b>{form.variations.length}</b></div>
            <div className="ab1-review-vars-wrap">
              {form.variations.map((v, i) => (
                <div key={i} className="ab1-review-var">
                  <div className="ab1-review-var-head">Variation {String.fromCharCode(65 + i)}</div>
                  <div className="ab1-kv"><span>Subject</span><b>{v.subject || '(empty)'}</b></div>
                  <div className="ab1-kv"><span>Preview text</span><b>{v.previewText || '—'}</b></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="ab1-review-card">
          <div className="ab1-review-title"><span>Design & Editor</span><button className="ab1-linkbtn" onClick={() => goToStep(1)}>Edit</button></div>
          <div className="ab1-review-body">
            <div className="ab1-kv"><span>Editor</span><b>{editorName}</b></div>
            <div className="ab1-kv"><span>Template</span><b>{templateName}</b></div>
          </div>
        </div>

        <div className="ab1-review-card">
          <div className="ab1-review-title"><span>Content</span><button className="ab1-linkbtn" onClick={() => goToStep(3)}>Edit</button></div>
          <div className="ab1-review-body ab1-review-content-row">
            <iframe className="ab1-review-thumb" title="preview" srcDoc={form.htmlContent} />
            <div className="ab1-review-content-info">
              <div className="ab1-kv"><span>HTML size</span><b>{(form.htmlContent || '').length} chars</b></div>
              <button className="ab1-btn ghost sm" onClick={() => flash('Test email sent (mock)')}>Send Test Email</button>
            </div>
          </div>
        </div>

        <div className="ab1-review-card">
          <div className="ab1-review-title"><span>Recipients & Winner</span><button className="ab1-linkbtn" onClick={() => goToStep(4)}>Edit</button></div>
          <div className="ab1-review-body">
            <div className="ab1-kv"><span>Recipients</span>
              <div className="ab1-tag-list">
                {form.recipientContacts.length === 0 ? <b>—</b> : form.recipientContacts.map(r => (<span key={r.type + r.id} className="ab1-review-tag">{r.label}</span>))}
              </div>
            </div>
            <div className="ab1-kv"><span>Exclude</span>
              <div className="ab1-tag-list">
                {form.excludeContacts.length === 0 ? <b>—</b> : form.excludeContacts.map(r => (<span key={r.type + r.id} className="ab1-review-tag">{r.label}</span>))}
              </div>
            </div>
            <div className="ab1-kv"><span>Eligible recipients</span><b>{eligibleCount}</b></div>
            <div className="ab1-kv"><span>Test audience size</span><b>{form.testSizePct}%</b></div>
            <div className="ab1-kv"><span>Winner delay</span><b>{form.winnerDelayValue} {form.winnerDelayUnit}</b></div>
          </div>
        </div>

        <div className="ab1-review-card">
          <div className="ab1-review-title"><span>Schedule & Tracking</span><button className="ab1-linkbtn" onClick={() => goToStep(6)}>Edit</button></div>
          <div className="ab1-review-body">
            <div className="ab1-kv"><span>Send mode</span><b>{form.sendMode === 'now' ? 'Send now' : 'Send later'}</b></div>
            <div className="ab1-kv"><span>When</span><b>{scheduleText}</b></div>
            {form.sendMode === 'later' && <div className="ab1-kv"><span>Timezone</span><b>{form.timezone} {getUtcOffset(form.timezone, form.scheduledDate)}</b></div>}
            <div className="ab1-kv"><span>Google Analytics</span><b>{form.gaTracking ? 'Enabled' : 'Disabled'}</b></div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== STEP 7: SCHEDULE ====================
  const renderStepSchedule = () => (
    <div className="ab1-card-form">
      <div className="ab1-schedule-panel">
        <h3>Schedule</h3>
        <div className="ab1-hint">Send it right now or schedule a time in the future.</div>

        <div className="ab1-radio-line" onClick={() => setForm({ ...form, sendMode: 'now' })}>
          <input type="radio" checked={form.sendMode === 'now'} onChange={() => {}} />
          <div>
            <div className="ab1-radio-title">Send now</div>
            <div className="ab1-hint">Immediately send your campaign.</div>
          </div>
        </div>

        <div className="ab1-radio-line" onClick={() => setForm({ ...form, sendMode: 'later' })}>
          <input type="radio" checked={form.sendMode === 'later'} onChange={() => {}} />
          <div style={{ flex: 1 }}>
            <div className="ab1-radio-title">Send later</div>
            <div className="ab1-hint">Set up a schedule to send your campaign at a specific time.</div>
            {form.sendMode === 'later' && (
              <div className="ab1-sched-later" onClick={e => e.stopPropagation()}>
                <div className="ab1-field-inline">
                  <label>Date, Time & Timezone</label>
                  <div className="ab1-sched-row">
                    <input type="date" value={form.scheduledDate} onChange={e => setForm({ ...form, scheduledDate: e.target.value })} />
                    <input type="time" value={form.scheduledTime} onChange={e => setForm({ ...form, scheduledTime: e.target.value })} />
                    <select value={form.timezone} onChange={e => setForm({ ...form, timezone: e.target.value })}>
                      {TIMEZONES.map(tz => (<option key={tz} value={tz}>{tz}</option>))}
                    </select>
                  </div>
                </div>
                {form.scheduledDate && (
                  <div className="ab1-info-banner amber">
                    <Icon name="info" size={14} /> Please note your UTC offset for selected timezone {form.timezone} on date {form.scheduledDate} is: {getUtcOffset(form.timezone, form.scheduledDate)}.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="ab1-schedule-divider" />

        <h4>Tracking configuration <Icon name="info" size={14} /></h4>
        <label className="ab1-checkbox-line">
          <input type="checkbox" checked={form.gaTracking} onChange={e => setForm({ ...form, gaTracking: e.target.checked })} />
          <span>Track campaign click report on Google Analytics <Icon name="info" size={14} /></span>
        </label>
      </div>
    </div>
  );

  // ==================== WIZARD SHELL ====================
  const renderStep = () => {
    if (wizardStep === 0) return renderStepSender();
    if (wizardStep === 1) return renderStepDesign();
    if (wizardStep === 2) return renderStepContent();
    if (wizardStep === 3) return renderStepRecipients();
    if (wizardStep === 4) return renderStepReview();
    if (wizardStep === 5) return renderStepSchedule();
    return null;
  };

  const renderWizardShell = () => {
    // Content step uses its own full-screen chrome
    if (wizardStep === 2) return renderStep();

    return (
      <div className="ab1-wiz">
        <div className="ab1-wiz-head">
          <div className="ab1-wiz-title">{STEPS[wizardStep]}</div>
          {renderStepper()}
        </div>
        <div className="ab1-wiz-body">{renderStep()}</div>
        <div className="ab1-wiz-foot">
          {wizardStep === 0 ? (<button className="ab1-btn ghost" onClick={cancelWizard}>Cancel</button>) : (<button className="ab1-btn ghost" onClick={goBack}>‹ Back</button>)}
          <div style={{ flex: 1 }} />
          {wizardStep === STEPS.length - 1 ? (
            <>
              <button className="ab1-btn ghost" onClick={saveDraft}>Save as Draft</button>
              <button className="ab1-btn primary" onClick={() => setConfirmOpen(true)}>Send</button>
            </>
          ) : (<button className="ab1-btn primary" onClick={tryNext}>Next: {STEPS[wizardStep + 1]}</button>)}
        </div>
      </div>
    );
  };

  // ==================== CONFIRMATION MODAL ====================
  const renderConfirmModal = () => {
    const sendWhen = form.sendMode === 'now' ? 'right now' : ('on ' + fmtSendDate(form.scheduledDate, form.scheduledTime, form.timezone));
    return (
      <div className="ab1-modal-overlay" onClick={() => setConfirmOpen(false)}>
        <div className="ab1-modal" onClick={e => e.stopPropagation()}>
          <div className="ab1-modal-row">
            <div className="ab1-modal-icon"><Icon name="warn" size={28} /></div>
            <div className="ab1-modal-body">
              <h3>Are you sure?</h3>
              <p>You are about to submit campaign <b>{form.name || '(unnamed)'}</b> which will be sent {sendWhen}.</p>
            </div>
          </div>
          <div className="ab1-modal-actions">
            <button className="ab1-btn ghost" onClick={() => setConfirmOpen(false)}>Cancel</button>
            <button className="ab1-btn primary" onClick={submitTest} disabled={loading}>Submit</button>
          </div>
        </div>
      </div>
    );
  };

  // ==================== DETAIL VIEW ====================
  const renderDetailView = () => {
    if (!detailTest) return null;
    const t = detailTest;
    const stats = t.stats || {};
    const opens = stats.opens != null ? stats.opens : (t.opens != null ? t.opens : 0);
    const clicks = stats.clicks != null ? stats.clicks : (t.clicks != null ? t.clicks : 0);
    const sent = stats.sent != null ? stats.sent : (t.sent != null ? t.sent : 0);
    const bounced = stats.bounced != null ? stats.bounced : (t.bounced != null ? t.bounced : 0);
    const unsubscribed = stats.unsubscribed != null ? stats.unsubscribed : (t.unsubscribed != null ? t.unsubscribed : 0);
    const spam = stats.spam != null ? stats.spam : (t.spam != null ? t.spam : 0);
    const openRate = sent > 0 ? ((opens / sent) * 100).toFixed(1) : '0.0';
    const clickRate = sent > 0 ? ((clicks / sent) * 100).toFixed(1) : '0.0';
    const detailSender = senders.find(s => (s._id || s.id) === t.senderId);
    const detailSenderLabel = detailSender ? ((detailSender.name || detailSender.fromName || 'Sender') + ' <' + (detailSender.email || detailSender.fromEmail || '') + '>') : (t.senderId || '—');
    return (
      <div className="ab1-detail">
        <div className="ab1-detail-head">
          <button className="ab1-btn ghost" onClick={closeDetail}>‹ Back to A/B Split Tests</button>
          <div className="ab1-detail-actions">
            <button className="ab1-btn ghost" onClick={() => { closeDetail(); editExisting(t); }}>Edit campaign</button>
            <button className="ab1-btn ghost" onClick={() => { closeDetail(); duplicateTest(t._id); }}>Duplicate</button>
            <button className="ab1-btn ghost" onClick={() => { archiveTest(t._id); closeDetail(); }}>Archive</button>
            <button className="ab1-btn danger" onClick={() => { deleteTest(t._id); closeDetail(); }}>Delete</button>
          </div>
        </div>

        <div className="ab1-detail-title-row">
          <div>
            <h1 className="ab1-detail-title">{t.name || '(unnamed)'}</h1>
            <div className="ab1-detail-subtitle"><span className={'ab1-status ' + (t.status || 'draft')}>{fmtStatus(t.status || 'draft')}</span><span> · {(t.variations || []).length} variations · Test size {t.testSizePct || 10}%</span></div>
          </div>
        </div>

        <div className="ab1-metrics-grid">
          <div className="ab1-metric-card"><div className="ab1-metric-label">Sent</div><div className="ab1-metric-value">{sent}</div></div>
          <div className="ab1-metric-card"><div className="ab1-metric-label">Opens</div><div className="ab1-metric-value">{opens}</div><div className="ab1-metric-sub">{openRate}%</div></div>
          <div className="ab1-metric-card"><div className="ab1-metric-label">Clicks</div><div className="ab1-metric-value">{clicks}</div><div className="ab1-metric-sub">{clickRate}%</div></div>
          <div className="ab1-metric-card"><div className="ab1-metric-label">Bounced</div><div className="ab1-metric-value">{bounced}</div></div>
          <div className="ab1-metric-card"><div className="ab1-metric-label">Unsubscribed</div><div className="ab1-metric-value">{unsubscribed}</div></div>
          <div className="ab1-metric-card"><div className="ab1-metric-label">Spam</div><div className="ab1-metric-value">{spam}</div></div>
        </div>

        <div className="ab1-detail-section">
          <h3>Variations Performance</h3>
          <div className="ab1-variations-grid">
            {(t.variations || []).map((v, i) => {
              const vs = (t.variationStats && t.variationStats[i]) || {};
              const vSent = vs.sent || 0;
              const vOpens = vs.opens || 0;
              const vClicks = vs.clicks || 0;
              const vOpenRate = vSent > 0 ? ((vOpens / vSent) * 100).toFixed(1) : '0.0';
              const vClickRate = vSent > 0 ? ((vClicks / vSent) * 100).toFixed(1) : '0.0';
              const isWinner = t.winnerVariationIndex === i;
              return (
                <div key={i} className={'ab1-variation-card ' + (isWinner ? 'winner' : '')}>
                  <div className="ab1-variation-head">
                    <div className="ab1-variation-badge">Variation {String.fromCharCode(65 + i)}</div>
                    {isWinner && <div className="ab1-winner-badge">🏆 Winner</div>}
                  </div>
                  <div className="ab1-variation-subject"><b>Subject:</b> {v.subject || '(empty)'}</div>
                  {v.previewText && <div className="ab1-variation-preview"><b>Preview:</b> {v.previewText}</div>}
                  <div className="ab1-variation-metrics">
                    <div><span>Sent</span><b>{vSent}</b></div>
                    <div><span>Opens</span><b>{vOpens} ({vOpenRate}%)</b></div>
                    <div><span>Clicks</span><b>{vClicks} ({vClickRate}%)</b></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="ab1-detail-section">
          <h3>Campaign Details</h3>
          <div className="ab1-detail-grid">
            <div className="ab1-kv"><span>Sender</span><b>{detailSenderLabel}</b></div>
            <div className="ab1-kv"><span>Editor</span><b>{(EDITORS.find(e => e.key === t.editor) || {}).title || t.editor || '—'}</b></div>
            <div className="ab1-kv"><span>Template</span><b>{t.templateId === 'empty' ? 'Empty (Custom HTML)' : ((templates.find(x => (x._id || x.key || x.name) === t.templateId) || {}).name || t.templateId || '—')}</b></div>
            <div className="ab1-kv"><span>Winner delay</span><b>{t.winnerDelayValue || 1} {t.winnerDelayUnit || 'Days'}</b></div>
            <div className="ab1-kv"><span>Send mode</span><b>{t.sendMode === 'now' ? 'Send now' : (t.sendMode === 'later' ? 'Send later' : '—')}</b></div>
            {t.sendMode === 'later' && <div className="ab1-kv"><span>Scheduled</span><b>{fmtSendDate(t.scheduledDate, t.scheduledTime, t.timezone)}</b></div>}
            <div className="ab1-kv"><span>Google Analytics</span><b>{t.gaTracking ? 'Enabled' : 'Disabled'}</b></div>
            <div className="ab1-kv"><span>Created</span><b>{t.createdAt ? new Date(t.createdAt).toLocaleString() : '—'}</b></div>
          </div>
        </div>

        <div className="ab1-detail-section">
          <h3>Recipients</h3>
          <div className="ab1-detail-tags">
            <div><b>Included:</b> {(t.recipientContacts || []).length === 0 ? ' —' : (t.recipientContacts || []).map(r => (<span key={r.type + r.id} className="ab1-review-tag">{r.label}</span>))}</div>
            <div><b>Excluded:</b> {(t.excludeContacts || []).length === 0 ? ' —' : (t.excludeContacts || []).map(r => (<span key={r.type + r.id} className="ab1-review-tag">{r.label}</span>))}</div>
          </div>
        </div>

        <div className="ab1-detail-section">
          <h3>Email Preview</h3>
          <iframe className="ab1-detail-preview" title="detail-preview" srcDoc={t.htmlContent || ''} />
        </div>
      </div>
    );
  };

  // ==================== ROOT RETURN ====================
  return (
    <div className="abtest-v1">
      {err && <div className="ab1-flash err">{err}</div>}
      {ok && <div className="ab1-flash ok">{ok}</div>}
      {detailTest ? renderDetailView() : (view === 'list' ? renderListView() : renderWizardShell())}
      {confirmOpen && renderConfirmModal()}
    </div>
  );
}
